import { z } from "zod";

/**
 * E-posta şablon sözleşmesi (ADR-0011 E3, CLOS-316).
 *
 * Şablon parametreleri `packages/shared` içinde tip güvenli tanımlanır: hem sunucu hem
 * (ileride) istemci aynı sözleşmeyi görür. Harici şablon servisi kullanılmaz — metin
 * değişikliği code review ve sürüm geçmişi görsün.
 */

/** Desteklenen diller. Bugün tek dil var; alan şimdiden sözleşmede ki sonradan eklenmesin. */
export const MAIL_LOCALES = ["tr"] as const;
export type MailLocale = (typeof MAIL_LOCALES)[number];

export const welcomeMailParams = z.object({
  tenantName: z.string().min(1, "Firma adı zorunludur"),
});
export type WelcomeMailParams = z.infer<typeof welcomeMailParams>;

export const passwordResetMailParams = z.object({
  resetUrl: z.url("Sıfırlama bağlantısı geçerli bir adres olmalıdır"),
  /** Bağlantının kaç dakika geçerli olduğu — kullanıcı aciliyeti bilsin. */
  expiresInMinutes: z.number().int().positive(),
});
export type PasswordResetMailParams = z.infer<typeof passwordResetMailParams>;

/**
 * Şablon kimlikleri. Sürüm ayrı bir alandır: bir şablonun metni değiştiğinde kimliği
 * DEĞİŞMEZ, sürümü artar. Böylece "hangi metin gönderildi?" sorusu geçmişe dönük
 * cevaplanabilir kalır.
 */
export const emailVerificationMailParams = z.object({
  verifyUrl: z.url("Doğrulama bağlantısı geçerli bir adres olmalıdır"),
  /** Bağlantının kaç saat geçerli olduğu — kullanıcı aciliyeti bilsin. */
  expiresInHours: z.number().int().positive(),
});
export type EmailVerificationMailParams = z.infer<typeof emailVerificationMailParams>;

/**
 * Hesaba yeni bir giriş yöntemi eklendiğinde gider (CLOS-357).
 *
 * Sağlayıcı ADI dışında hiçbir sağlayıcı verisi taşımaz: e-posta metni de bir sızıntı
 * yüzeyidir ve posta kutusu saldırganın elindeyse okunur.
 */
export const identityLinkedMailParams = z.object({
  provider: z.string().min(1, "Sağlayıcı adı zorunludur"),
});
export type IdentityLinkedMailParams = z.infer<typeof identityLinkedMailParams>;

/**
 * Hesap durumu değiştiğinde gider (CLOS-282).
 *
 * Yalnız YENİ durumu taşır; gerekçe metni taşımaz. Gerekçe iç kayıt için yazılır ve
 * serbest metindir — posta kutusu saldırganın elindeyse okunacak bir yere serbest metin
 * koymak yeni bir sızıntı yüzeyi açardı.
 */
export const accountStatusChangedMailParams = z.object({
  status: z.string().min(1, "Durum zorunludur"),
});
export type AccountStatusChangedMailParams = z.infer<typeof accountStatusChangedMailParams>;

/**
 * Şifre değiştiğinde gider (CLOS-64). Hiçbir parametre TAŞIMAZ: metin tek ve sabit,
 * taşınacak veri yok. Boş bir nesne, "ileride bir şey eklenirse" diye alan açmaktan iyi.
 */
export const passwordChangedMailParams = z.object({});
export type PasswordChangedMailParams = z.infer<typeof passwordChangedMailParams>;

/**
 * Profilde kritik bir alan değiştiğinde gider (CLOS-65).
 *
 * Yalnız DEĞİŞEN ALANLARIN adlarını taşır, yeni değerleri değil: posta kutusu saldırganın
 * elindeyse okunur ve yeni telefon numarasını oraya yazmak yeni bir sızıntı olurdu.
 */
export const profileChangedMailParams = z.object({
  fields: z.array(z.string()).min(1, "En az bir alan gereklidir"),
});
export type ProfileChangedMailParams = z.infer<typeof profileChangedMailParams>;

/**
 * Rol yetkileri değişti bildirimi (CLOS-86 `[029]`).
 *
 * Kart: *"etkilenen kullanıcılar ve yetkili yöneticiler bilgilendirilir."* Kullanıcı bir
 * sabah ürünün yarısını göremediğinde sebebini bilmelidir; sessiz bir yetki daralması,
 * arıza ile ayırt edilemez.
 */
export const roleChangedMailParams = z.object({
  roleName: z.string().min(1, "Rol adı gereklidir"),
  added: z.array(z.string()),
  removed: z.array(z.string()),
});
export type RoleChangedMailParams = z.infer<typeof roleChangedMailParams>;

/**
 * Organizasyon daveti bildirimi (CLOS-73 `[034]`).
 *
 * Bağlantı ham token taşıyor ve yalnız e-postada bulunuyor: sunucuda özeti duruyor.
 */
export const organizationInviteMailParams = z.object({
  organizationName: z.string().min(1, "Organizasyon adı gereklidir"),
  inviteUrl: z.url("Geçerli bir bağlantı gereklidir"),
  expiresAt: z.string().min(1, "Son geçerlilik gereklidir"),
});
export type OrganizationInviteMailParams = z.infer<typeof organizationInviteMailParams>;

/**
 * Müvekkil Temsilcisi daveti bildirimi (CLOS-177 `[124]`).
 *
 * Bağlantı ham token taşıyor ve yalnız e-postada bulunuyor: sunucuda özeti duruyor.
 * Metin MÜVEKKİL ADINI da taşıyor: davetli kimin adına çağrıldığını bilmeden bağlantıya
 * tıklamamalı — organizasyon davetiyle aynı oltalama gerekçesi.
 */
export const representativeInviteMailParams = z.object({
  organizationName: z.string().min(1, "Organizasyon adı gereklidir"),
  clientDisplayName: z.string().min(1, "Müvekkil adı gereklidir"),
  inviteUrl: z.url("Geçerli bir bağlantı gereklidir"),
  expiresAt: z.string().min(1, "Son geçerlilik gereklidir"),
});
export type RepresentativeInviteMailParams = z.infer<typeof representativeInviteMailParams>;

/**
 * Haricî sözleşme katılımcısı daveti bildirimi (CLOS-145 `[126]`).
 *
 * ⚠ SÖZLEŞME BAŞLIĞI e-postaya girmiyor: başlık tek başına ticari bilgi olabilir ve
 * e-posta, alıcının kutusunda şifresiz durur. Davetli neye çağrıldığını organizasyon
 * adından ve token'lı görüntüleme ekranından (kapısı olan yerden) öğrenir.
 */
export const participantInviteMailParams = z.object({
  organizationName: z.string().min(1, "Organizasyon adı gereklidir"),
  inviteUrl: z.url("Geçerli bir bağlantı gereklidir"),
  expiresAt: z.string().min(1, "Son geçerlilik gereklidir"),
});
export type ParticipantInviteMailParams = z.infer<typeof participantInviteMailParams>;

/**
 * SÖZLEŞME ORGANİZASYON DAVETİ bildirimi (CLOS-1093).
 *
 * ### ⚠ Neden var: davet vardı, haber yoktu
 *
 * Sözleşme organizasyon daveti kaydediliyor ve olay yayılıyordu ama **hiç e-posta
 * gitmiyordu**. Karşı organizasyon daveti ancak Closer'a girip *Gelen Davetler* sayfasına
 * bakarsa görüyordu; kimse ona haber vermiyordu ve davet süresi sessizce dolabiliyordu.
 *
 * ### ⚠ SÖZLEŞME BAŞLIĞI E-POSTAYA GİRMİYOR
 *
 * `participantInviteMailParams` ile aynı kural ve aynı gerekçe: başlık tek başına ticari
 * bilgi olabilir ve e-posta alıcının kutusunda şifresiz durur. Davet eden organizasyonun
 * adı, davetlinin bağlantıya güvenip güvenmeyeceğine karar vermesi için yeterli; neye
 * çağrıldığını kapısı olan ekranda öğreniyor.
 *
 * ⚠ Bağlantı TOKEN TAŞIMIYOR: haricî kişi davetinden farkı bu. Organizasyon daveti zaten
 * hedef kiracının kendi oturumundan kabul ediliyor (`/api/org-invitations/incoming`,
 * kapısı `contract.share`); e-postaya token koysaydık, oturumdan bağımsız ikinci bir kabul
 * yolu açılırdı ve kapı iki yerde yaşardı.
 */
export const contractOrgInviteMailParams = z.object({
  invitingOrganizationName: z.string().min(1, "Davet eden organizasyon adı gereklidir"),
  /**
   * ⚠ Sözleşmenin BAŞLIĞI — ve başlıktan başka hiçbir alanı. Kart e-postanın *"hangi
   * sözleşme için geldiğini"* söylemesini istiyor; alıcının kutusunda duran bir metnin
   * taşıyabileceği sınır da tam burada bitiyor: sözleşme metni, iç notlar ve taraf
   * listesi girmiyor (raporun 36. bölümü).
   *
   * ⚠ `min(1)`: boş bir başlık, metinde tırnak içinde boşluk bırakır ve alıcı hangi
   * sözleşmeden söz edildiğini anlayamaz. Çözülemediği durumda çağıran taraf nötr bir
   * ifade koyuyor ve olayı uyarıya çeviriyor — sessiz boşluk kabul edilmiyor.
   */
  contractTitle: z.string().min(1, "Sözleşme başlığı gereklidir"),
  inviteUrl: z.url("Geçerli bir bağlantı gereklidir"),
  expiresAt: z.string().min(1, "Son geçerlilik gereklidir"),
});
export type ContractOrgInviteMailParams = z.infer<typeof contractOrgInviteMailParams>;

/**
 * Hizmet ilişkisi talebi e-postası (CLOS-1105 · K1 · ADR-0067).
 *
 * ⚠ Alıcı bir ADRES: talep hangi organizasyon adına kabul edilecekse onu alıcı kendi
 * oturumunda seçiyor; metin de bunu söylüyor. Bağlantı TOKEN taşımıyor — kabul kararı
 * oturumda (`contractOrgInvite` ile aynı gerekçe).
 *
 * ⚠ ÖZEL VERİ TAŞINMIYOR: kapsam AÇIKLAMASI (`scopeDetail`), müvekkil kaydının adı ve
 * iç notlar girmiyor (R4 §6.6). Metin talep eden organizasyonun adı ve kapsam etiketinden
 * ibaret — alıcının kutusunda şifresiz duran bir metnin taşıyabileceği kadarı bu.
 */
export const serviceRelationshipRequestMailParams = z.object({
  requestingOrganizationName: z.string().min(1, "Talep eden organizasyon adı gereklidir"),
  scopeLabel: z.string().min(1, "Hizmet kapsamı gereklidir"),
  inviteUrl: z.url("Geçerli bir bağlantı gereklidir"),
  expiresAt: z.string().min(1, "Son geçerlilik gereklidir"),
});
export type ServiceRelationshipRequestMailParams = z.infer<
  typeof serviceRelationshipRequestMailParams
>;

/**
 * Üyelik durumu değişti bildirimi (CLOS-77 `[038]`).
 *
 * Kart: *"bildirim ve audit oluşur."* Erişimi bir sabah kaybolan kullanıcı sebebini
 * bilmelidir; sessiz bir askı, arıza ile ayırt edilemez ve destek kaydına dönüşür.
 *
 * Gerekçenin **etiketi** taşınıyor, kodu değil: `security_review` kullanıcı için bir şey
 * ifade etmiyor, "Güvenlik incelemesi" ediyor. Serbest metin NOT taşınmıyor — yönetici
 * notu iç bağlamdır ve e-postaya konsaydı kullanıcı hakkında yazılmış bir metin, kimsenin
 * öyle olduğunu düşünmediği bir anda kendisine gönderilirdi.
 */
export const membershipStatusChangedMailParams = z.object({
  organizationName: z.string().min(1, "Organizasyon adı gereklidir"),
  status: z.string().min(1, "Durum zorunludur"),
  reasonLabel: z.string().min(1, "Gerekçe gereklidir"),
});
export type MembershipStatusChangedMailParams = z.infer<typeof membershipStatusChangedMailParams>;

/**
 * Üyeliğin ROLÜ değişti bildirimi (CLOS-89 `[039]`).
 *
 * `roleChanged` ile karıştırılmamalı: o, bir ROLÜN yetkilerinin değişmesidir ve o rolü
 * taşıyan herkese gider. Bu ise TEK kullanıcının rolünün değişmesi. İkisini tek şablona
 * sıkıştırmak, kullanıcıya "yetkileriniz değişti" deyip hangisinin olduğunu söylememek
 * olurdu.
 *
 * Yetki listesi TAŞINMIYOR: yeni rolün yetkilerini e-postaya dökmek, kullanıcının
 * göremediği bir yüzeyin haritasını posta kutusuna koymak olurdu. Rol ADI yeterli.
 */
export const membershipRoleChangedMailParams = z.object({
  organizationName: z.string().min(1, "Organizasyon adı gereklidir"),
  roleName: z.string().min(1, "Rol adı gereklidir"),
});
export type MembershipRoleChangedMailParams = z.infer<typeof membershipRoleChangedMailParams>;

/**
 * Bildirim e-postası (CLOS-187 `[169]`).
 *
 * ### ⚠ GÖVDE YOK, YALNIZ BAŞLIK VE KATEGORİ
 *
 * Uygulama içi bildirim kaydı bile gövdeyi saklamıyor (CLOS-186): başlık katalogdan
 * geliyor ve kişisel veri taşımıyor. E-posta bunu KOPYALAYAMAZ — posta kutusu erişim
 * kontrolünün DIŞINDA bir yer; sözleşme başlığını, yorum metnini ya da taraf adını oraya
 * yazmak, "erişemediği kayıt bilgisi bildirimde sızmaz" kriterini uygulamanın dışına
 * taşıyarak çiğnerdi.
 *
 * ⚠ Bağlantı da KAYDA DEĞİL uygulamaya: kullanıcı önce giriş yapıyor, erişim orada
 * yeniden sorulacak. Doğrudan kayıt adresi verseydik, posta kutusuna erişen biri hangi
 * kaydın var olduğunu öğrenirdi.
 */
export const notificationMailParams = z.object({
  categoryName: z.string().min(1, "Kategori adı gereklidir"),
  title: z.string().min(1, "Başlık gereklidir"),
  organizationName: z.string().min(1, "Organizasyon adı gereklidir"),
});
export type NotificationMailParams = z.infer<typeof notificationMailParams>;

/**
 * Yeni adrese giden doğrulama bağlantısı (CLOS-225 `[048]`).
 *
 * `emailVerification`'dan AYRI bir şablon: orada mesaj "hesabını doğrula", burada "bu
 * adresi hesabına bağlamak istediğini doğrula". Aynı şablonu iki anlamda kullanmak,
 * kullanıcıya neyi onayladığını yanlış söylerdi.
 */
export const emailChangeVerificationMailParams = z.object({
  verifyUrl: z.url("Doğrulama bağlantısı geçerli bir adres olmalıdır"),
  expiresInHours: z.number().int().positive("Geçerlilik süresi pozitif olmalıdır"),
});
export type EmailChangeVerificationMailParams = z.infer<typeof emailChangeVerificationMailParams>;

/**
 * ESKİ adrese giden güvenlik bildirimi (CLOS-225 `[048]`).
 *
 * Talep ANINDA gönderiliyor, tamamlandığında değil: değişikliği yapan kişi hesabı ele
 * geçiren biriyse, sahibinin müdahale edebileceği tek pencere bu. Tamamlandıktan sonra
 * gelen bir bildirim yalnız haber verir; bu bildirim durdurabilir.
 *
 * ⚠ Yeni adres MASKELİ gönderiliyor: bildirimi okuyan kişi hesabı ele geçirmeye
 * çalışan biri DEĞİL, kurbandır — ama posta kutusu da ele geçmiş olabilir ve saldırganın
 * hedef adresini tam olarak öğrenmesine gerek yok (`[034]` maskeleme kararıyla aynı).
 */
export const emailChangeRequestedMailParams = z.object({
  maskedNewEmail: z.string().min(1, "Maskelenmiş adres gereklidir"),
  expiresInHours: z.number().int().positive("Geçerlilik süresi pozitif olmalıdır"),
});
export type EmailChangeRequestedMailParams = z.infer<typeof emailChangeRequestedMailParams>;

export const MAIL_TEMPLATES = {
  welcome: "welcome",
  passwordReset: "password-reset",
  emailVerification: "email-verification",
  emailChangeVerification: "email-change-verification",
  emailChangeRequested: "email-change-requested",
  identityLinked: "identity-linked",
  accountStatusChanged: "account-status-changed",
  passwordChanged: "password-changed",
  profileChanged: "profile-changed",
  roleChanged: "role-changed",
  organizationInvite: "organization-invite",
  representativeInvite: "representative-invite",
  participantInvite: "participant-invite",
  contractOrgInvite: "contract-org-invite",
  /** Hizmet ilişkisi talebi — e-postayla adreslenen karşı tarafa (CLOS-1105). */
  serviceRelationshipRequest: "service-relationship-request",
  membershipStatusChanged: "membership-status-changed",
  membershipRoleChanged: "membership-role-changed",
  notification: "notification",
} as const;
export type MailTemplateId = (typeof MAIL_TEMPLATES)[keyof typeof MAIL_TEMPLATES];
